import { Check, Copy } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Button } from '../../../../components/Button';
import { ConfirmDialog } from '../../../../components/ConfirmDialog';
import { Modal } from '../../../../components/Modal';
import { ApiError } from '../../../../lib/apiError';
import { usersApi } from '../usersApi';
import type { UserCredentialIssued, UserListItem } from '../userTypes';

export type UserConfirmKind = 'deactivate' | 'activate' | 'reset-password';

interface Request {
  kind: UserConfirmKind;
  user: UserListItem;
}

/**
 * The row actions that need confirmation before they run. Each is triggered from the row
 * menu — outside any modal — so `<ConfirmDialog>` is the right shape here.
 */
export function UserConfirmDialog({
  request,
  onClose,
  onDone,
}: {
  request: Request | null;
  onClose: () => void;
  onDone: () => void;
}) {
  const [running, setRunning] = useState(false);
  const [error, setError] = useState<ApiError | null>(null);
  const [issued, setIssued] = useState<UserCredentialIssued | null>(null);

  useEffect(() => {
    setError(null);
    setIssued(null);
  }, [request]);

  // A reset returns a one-time password, so it takes over the dialog rather than closing.
  if (issued) {
    return (
      <Modal
        open
        onClose={() => {
          setIssued(null);
          onDone();
        }}
        title="Password reset"
        size="lg"
        id="user-reset-issued-modal"
        footer={
          <Button
            id="user-reset-issued-done"
            variant="primary"
            onClick={() => {
              setIssued(null);
              onDone();
            }}
          >
            Done
          </Button>
        }
      >
        <IssuedPassword issued={issued} />
      </Modal>
    );
  }

  if (!request) return null;

  const { kind, user } = request;
  const copy = COPY[kind];

  async function run() {
    if (!request) return;

    setRunning(true);
    setError(null);

    try {
      if (kind === 'reset-password') {
        const result = await usersApi.resetPassword(user.id, { mustChangePassword: true });
        setIssued(result);
        return;
      }

      if (kind === 'deactivate') await usersApi.deactivate(user.id);
      if (kind === 'activate') await usersApi.activate(user.id);

      onDone();
    } catch (caught) {
      setError(caught instanceof ApiError ? caught : new ApiError(copy.failure, 0));
    } finally {
      setRunning(false);
    }
  }

  return (
    <ConfirmDialog
      open
      onClose={onClose}
      onConfirm={() => void run()}
      title={copy.title}
      danger={copy.danger}
      // Destructive kinds only —  already marks exactly those. Activating an
      // account is the undo, and needs no ceremony.
      requireConfirm={copy.danger}
      requireCredential={copy.danger}
      loading={running}
      id={`user-${kind}-dialog`}
      body={
        <div className="flex flex-col gap-2">
          <p>{copy.body(user.displayName)}</p>
          {error ? (
            <p role="alert" className="rounded-lg border border-red-200 bg-red-50 p-2 text-xs text-red-700">
              {error.message}
            </p>
          ) : null}
        </div>
      }
    />
  );
}

const COPY: Record<
  UserConfirmKind,
  {
    title: string;
    body: (name: string) => string;
    confirmLabel: string;
    confirmingLabel: string;
    danger: boolean;
    failure: string;
  }
> = {
  deactivate: {
    title: 'Deactivate user',
    body: (name) =>
      `${name} will be signed out everywhere and will no longer be able to sign in. ` +
      'The account is kept and can be restored.',
    confirmLabel: 'Deactivate',
    confirmingLabel: 'Deactivating',
    danger: true,
    failure: 'Could not deactivate the account.',
  },
  activate: {
    title: 'Restore user',
    body: (name) => `${name} will be able to sign in again with their existing password.`,
    confirmLabel: 'Restore',
    confirmingLabel: 'Restoring',
    danger: false,
    failure: 'Could not restore the account.',
  },
  'reset-password': {
    title: 'Reset password',
    body: (name) =>
      `A new password will be generated for ${name} and shown once. Every existing session ` +
      'is signed out, and they must change it at next sign-in.',
    confirmLabel: 'Reset',
    confirmingLabel: 'Resetting',
    danger: true,
    failure: 'Could not reset the password.',
  },
};

/** Shown exactly once — the server does not retain the value. */
function IssuedPassword({ issued }: { issued: UserCredentialIssued }) {
  const [copied, setCopied] = useState(false);

  async function copyValue() {
    if (!issued.generatedPassword) return;

    try {
      await navigator.clipboard.writeText(issued.generatedPassword);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // Clipboard access can be refused; the value is on screen regardless.
    }
  }

  return (
    <div className="flex flex-col gap-4">
      <p className="text-sm text-slate-600">
        The password for <span className="font-mono tabular-nums">{issued.email}</span> has been
        reset.
      </p>

      {issued.generatedPassword ? (
        <div className="rounded-lg border border-amber-200 bg-amber-50 p-3">
          <p className="text-xs font-medium text-amber-800">
            This password is shown once and is not stored anywhere. Copy it now and deliver it
            through a channel you trust.
          </p>

          <div className="mt-2.5 flex items-center gap-2">
            <code className="min-w-0 flex-1 truncate rounded-md border border-amber-200 bg-white px-3 py-2 font-mono tabular-nums text-sm text-slate-800">
              {issued.generatedPassword}
            </code>
            <Button
              id="user-reset-copy"
              variant="secondary"
              onClick={() => void copyValue()}
              leading={
                copied ? <Check className="h-4 w-4 text-emerald-600" /> : <Copy className="h-4 w-4" />
              }
            >
              {copied ? 'Copied' : 'Copy'}
            </Button>
          </div>
        </div>
      ) : null}
    </div>
  );
}
